import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  TextField,
  Box,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CampaignTypeSelection from './CampaignTypeSelection';
import EventTypeSelection from './EventTypeSelection';
import BirthdayCampaignSettings from './BirthdayCampaignSettings';
import CampaignDateSettings from './CampaignDateSettings';

type CampaignType = 'ONE_TIME' | 'RECURRING' | '';
type LocalEventType = 'BIRTHDAY' | 'CIRCLE_UPGRADE' | 'EMAIL_OPT_IN' | 'REGISTRATION' | '';

interface CampaignBasicInfoProps {
  name: string;
  type: CampaignType;
  eventType?: LocalEventType;
  executionHour: string;
  daysOffset: number | null;
  executionDateTime?: string;
  onNameChange: (name: string) => void;
  onCampaignTypeChange: (type: CampaignType) => void;
  onEventTypeChange: (type: LocalEventType) => void;
  onExecutionDateChange: (value: string) => void;
  onBirthdayOffsetChange: (offset: number | null) => void;
  onBirthdayTimeChange: (time: string) => void;
  nameError?: string;
  disabled?: boolean;
}

const CampaignBasicInfo: React.FC<CampaignBasicInfoProps> = ({
  name,
  type,
  eventType,
  executionHour,
  daysOffset,
  executionDateTime,
  onNameChange,
  onCampaignTypeChange,
  onEventTypeChange,
  onExecutionDateChange,
  onBirthdayOffsetChange,
  onBirthdayTimeChange,
  nameError,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isRecurring = type === 'RECURRING';
  const showExecutionDate = type === 'ONE_TIME';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <InfoOutlined color="primary" />
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            {t('campaignExperimental.create.campaignInformation')}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('campaignExperimental.create.campaignInformationDesc')}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('campaignExperimental.create.campaignNameLabel')}
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              error={!!nameError}
              helperText={nameError || t('campaignExperimental.create.campaignNameHelp')}
              placeholder={t('campaignExperimental.create.campaignNamePlaceholder')}
              variant="outlined"
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12}>
            <CampaignTypeSelection
              value={type === 'RECURRING' ? 'event_driven' : 'scheduled'}
              onChange={(selection) =>
                onCampaignTypeChange(selection === 'event_driven' ? 'RECURRING' : 'ONE_TIME')
              }
              disabled={disabled}
            />
          </Grid>

          {isRecurring && (
            <Grid item xs={12}>
              <EventTypeSelection
                value={(() => {
                  if (eventType === 'BIRTHDAY') return 'birthday';
                  if (eventType === 'CIRCLE_UPGRADE') return 'tier_update';
                  if (eventType === 'EMAIL_OPT_IN') return 'newsletter_opt_in';
                  if (eventType === 'REGISTRATION') return 'registration';
                  return '';
                })()}
                onChange={(selection) => {
                  let mapped: LocalEventType = '';
                  if (selection === 'birthday') mapped = 'BIRTHDAY';
                  else if (selection === 'tier_update') mapped = 'CIRCLE_UPGRADE';
                  else if (selection === 'newsletter_opt_in') mapped = 'EMAIL_OPT_IN';
                  else if (selection === 'registration') mapped = 'REGISTRATION';
                  onEventTypeChange(mapped);
                }}
                disabled={disabled}
              />
            </Grid>
          )}

          {isRecurring && eventType === 'BIRTHDAY' && (
            <Grid item xs={12}>
              <BirthdayCampaignSettings
                daysOffset={daysOffset?.toString() || ''}
                distributionTime={executionHour}
                onDaysOffsetChange={(offset) =>
                  onBirthdayOffsetChange(offset ? Number.parseInt(offset, 10) : null)
                }
                onDistributionTimeChange={onBirthdayTimeChange}
                disabled={disabled}
              />
            </Grid>
          )}

          {showExecutionDate && (
            <Grid item xs={12}>
              <CampaignDateSettings
                type="execution"
                executionDate={executionDateTime}
                onExecutionDateChange={onExecutionDateChange}
                disabled={disabled}
              />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CampaignBasicInfo;
